import { zodResolver } from '@hookform/resolvers/zod'
import dayjs, { type ManipulateType } from 'dayjs'
import { useForm, useWatch } from 'react-hook-form'

import { type ResCaseAlertItem, ResCaseAlertItemSchema } from '@/lib/queries/business/v1/alert/types'
import { getLocalDate } from '@/lib/utils/datetime'

import { caseAlertFormSchema } from '../constants'
import { type CaseAlertForm, type CaseAlertFormSchema } from '../types'

export interface UseAlertDialogParams {
  defaultValues: CaseAlertForm | ResCaseAlertItem
}

const isResCaseAlertItem = (defaultValues: CaseAlertForm | ResCaseAlertItem): defaultValues is ResCaseAlertItem => {
  return ResCaseAlertItemSchema.safeParse(defaultValues).success
}

export const useAlertDialog = ({ defaultValues }: UseAlertDialogParams) => {
  const form = useForm<CaseAlertFormSchema>({
    resolver: zodResolver(caseAlertFormSchema),
    defaultValues: isResCaseAlertItem(defaultValues)
      ? {
          alertType: defaultValues.alertType,
          alertDate: getLocalDate(dayjs.utc(defaultValues.deadline).format('YYYY-MM-DD')),
          notificationSettings: {
            count: `${defaultValues.timeAgo}`,
            unit: defaultValues.timeUnit,
          },
        }
      : defaultValues,
  })
  const formErrors = form.formState.errors
  const values = useWatch({ control: form.control })

  const checkIfNotificationDateIsAfterToday = ({
    alertDate,
    countValue,
    unitValue,
  }: {
    alertDate?: Date
    countValue?: string
    unitValue?: string
  }): {
    isAfter: boolean
    notificationDate: Date | undefined
    errorMessage: string | undefined
  } => {
    if (!alertDate || !countValue || !unitValue) {
      return {
        isAfter: false,
        notificationDate: undefined,
        errorMessage: undefined,
      }
    }

    const notificationDate =
      alertDate && unitValue && dayjs(alertDate).subtract(+countValue, unitValue as ManipulateType)

    const isAfter = !!notificationDate && notificationDate.isAfter(dayjs(), unitValue as ManipulateType)

    return {
      isAfter,
      notificationDate: notificationDate ? notificationDate.toDate() : undefined,
      errorMessage: !isAfter
        ? `Alert date(${dayjs(notificationDate).format(
            'MM-DD-YYYY'
          )}) must be after today's date(${dayjs().format('MM-DD-YYYY')}).`
        : undefined,
    }
  }

  const markAlertTypeError = (value: string) => {
    form.clearErrors('alertType')

    if (!value) {
      form.setError('alertType', {})
      return
    }
  }

  const markAlertDateError = (value?: Date | '') => {
    form.clearErrors('alertDate')

    if (!value) {
      form.setError('alertDate', {})
      return
    }

    const { isAfter, notificationDate, errorMessage } = checkIfNotificationDateIsAfterToday({
      alertDate: value,
      countValue: values.notificationSettings?.count,
      unitValue: values.notificationSettings?.unit,
    })

    if (!isAfter && notificationDate) {
      form.setError('alertDate', {
        message: errorMessage,
      })
      return
    }
  }

  const markCountError = (countValue: string) => {
    form.clearErrors('notificationSettings')

    if (/[^0-9]/i.test(countValue)) {
      form.setError('notificationSettings', {
        message: 'Count value must be a positive number.',
      })
      return
    }

    if (!countValue || !values.notificationSettings?.unit) {
      form.setError('notificationSettings', {})
      return
    }

    const { isAfter, notificationDate, errorMessage } = checkIfNotificationDateIsAfterToday({
      alertDate: values.alertDate,
      countValue,
      unitValue: values.notificationSettings.unit,
    })

    if (!isAfter && notificationDate) {
      form.setError('notificationSettings', {
        message: errorMessage,
      })
      return
    }
  }

  const markUnitError = (unitValue: string) => {
    form.clearErrors('notificationSettings')

    if (!unitValue || !values.notificationSettings?.count) {
      form.setError('notificationSettings', {})
      return
    }

    const { isAfter, notificationDate, errorMessage } = checkIfNotificationDateIsAfterToday({
      alertDate: values.alertDate,
      countValue: values.notificationSettings.count,
      unitValue,
    })

    if (!isAfter && notificationDate) {
      form.setError('notificationSettings', {
        message: errorMessage,
      })
      return
    }
  }

  return {
    form,
    formErrors,
    values,
    markAlertTypeError,
    markAlertDateError,
    markCountError,
    markUnitError,
  }
}
